// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Tenant from "./tenant.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "password": {
      "column": null,
      "name": "password",
      "selected_by_default": null,
      "translated": null
    },
    "sync_contacts": {
      "column": {
        "default": false,
        "name": "sync_contacts",
        "null": false,
        "type": "boolean"
      },
      "name": "sync_contacts",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "username": {
      "column": null,
      "name": "username",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "TenantUniSetting",
  "collectionKey": "tenant_uni_settings",
  "collectionName": "tenant_uni_settings",
  "i18nKey": "tenant_uni_setting",
  "camelizedLower": "tenantUniSetting",
  "name": "TenantUniSetting",
  "nameDasherized": "tenant-uni-setting",
  "pluralName": "tenant_uni_settings",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "encrypted_password",
      "column": {
        "default": null,
        "name": "encrypted_password",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_password_iv",
      "column": {
        "default": null,
        "name": "encrypted_password_iv",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_username",
      "column": {
        "default": null,
        "name": "encrypted_username",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_username_iv",
      "column": {
        "default": null,
        "name": "encrypted_username_iv",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "sync_contacts",
      "column": {
        "default": false,
        "name": "sync_contacts",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    }
  ],
  "paramKey": "tenant_uni_setting",
  "primaryKey": "id"
}

/** Frontend model for TenantUniSetting. */
export default class TenantUniSetting extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  password() {
    return this.readAttributeUnderscore("password")
  }

  /** @returns {boolean} */
  hasPassword() {
    const value = this.password()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  syncContacts() {
    return this.readAttributeUnderscore("sync_contacts")
  }

  /** @returns {boolean} */
  hasSyncContacts() {
    const value = this.syncContacts()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  username() {
    return this.readAttributeUnderscore("username")
  }

  /** @returns {boolean} */
  hasUsername() {
    const value = this.username()

    return this._isPresent(value)
  }

  /** @returns {import("./tenant.js").default | null} */
  tenant() {
    return this._readBelongsToReflection({modelClass: Tenant, reflectionName: "tenant"})
  }

  /** @returns {Promise<import("./tenant.js").default | null>} */
  loadTenant() {
    if (!("tenantId" in this)) throw new Error("Foreign key method wasn't defined: tenantId")

    const id = this.tenantId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "tenant",
        model: this,
        modelClass: Tenant
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./tenant.js").default | null>} */
  ensureTenantLoaded() {
    return this.ensureAssociationLoaded("tenant")
  }
}
